import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { RichText } from '@components/common/RichText';
import './MultipleChoiceQuestion.css';

/**
 * Multi-Select Question Component
 * Allows selecting multiple correct answers
 */
export function MultiSelectQuestion({ question, value = [], onChange, disabled = false, showCorrect = false }) {
  const shuffledOptions = useMemo(() => {
    if (!question?.options) return [];
    const copy = [...question.options];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, [question?.id, question?.options]);

  const getOptionKey = (option, index) => {
    if (option == null) return index;
    if (typeof option === 'string' || typeof option === 'number') return option;
    return option.id ?? option.value ?? option.key ?? option.text ?? index;
  };

  const handleOptionClick = (optionKey) => {
    if (disabled) return;
    
    const currentSelections = Array.isArray(value) ? value : [];
    const isSelected = currentSelections.includes(optionKey);
    
    if (isSelected) {
      onChange(currentSelections.filter(v => v !== optionKey));
    } else {
      onChange([...currentSelections, optionKey]);
    }
  };

  const correctAnswers = Array.isArray(question.correctAnswer) 
    ? question.correctAnswer 
    : [question.correctAnswer];

  return (
    <div className="multi-select-question">
      <div className="mb-3 text-muted small">
        <i className="bi bi-info-circle"></i> Select all that apply
      </div>
      {shuffledOptions.map((option, index) => {
        const optionKey = getOptionKey(option, index);
        const isSelected = Array.isArray(value) && value.includes(optionKey);
        const isCorrect = correctAnswers.includes(option?.id)
          || correctAnswers.includes(optionKey)
          || correctAnswers.includes(index)
          || (option?.value != null && correctAnswers.includes(option.value))
          || (option?.text != null && correctAnswers.includes(option.text));
        
        let variantClass = '';
        if (showCorrect) {
          if (isCorrect) {
            variantClass = 'option-correct';
          } else if (isSelected && !isCorrect) {
            variantClass = 'option-incorrect';
          }
        }

        return (
          <div
            key={option.id || index}
            className={`option-card ${isSelected ? 'selected' : ''} ${variantClass} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleOptionClick(optionKey)}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOptionClick(optionKey);
              }
            }}
          >
            <Form.Check
              type="checkbox"
              id={`ms-option-${question.id}-${index}`}
              checked={isSelected}
              onChange={() => {}}
              disabled={disabled}
              className="option-radio"
              tabIndex={-1}
            />
            <label 
              htmlFor={`ms-option-${question.id}-${index}`}
              className="option-label"
              onClick={(e) => e.preventDefault()}
            >
              <RichText value={option.text || option} />
            </label>
          </div>
        );
      })}
    </div>
  );
}
